import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah8p93oh653ivfvnohg";

export default function TipsForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的线索，感谢分享。");
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      params.append(key, String(value));
    });

    setStatus("submitting");
    setMessage("正在提交，请稍候…");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setMessage(serverMsg || "提交未成功，请稍后重试。");
        return;
      }

      setStatus("success");
      setMessage(parsed?.meta?.message || "已收到你的线索，编辑会在核实后回复。");
      form.reset();
      setCount(0);
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <form
      id="tips-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7"
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className="ri-mail-send-line text-xl" />
        </span>
        <div>
          <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
            提交你掌握的事件线索
          </h3>
          <p className="text-xs md:text-sm text-foreground-600 mt-0.5">
            可提供亲历细节、时间节点或公开来源链接，我们核实后会补进时间线。
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="tip-name" className="text-xs font-medium text-foreground-700">
            你的称呼
          </label>
          <input
            id="tip-name"
            name="contact_name"
            type="text"
            required
            placeholder="如：王先生"
            className="w-full text-sm px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="tip-email" className="text-xs font-medium text-foreground-700">
            联系邮箱
          </label>
          <input
            id="tip-email"
            name="email"
            type="email"
            required
            placeholder="用于接收核实回复"
            className="w-full text-sm px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="tip-topic" className="text-xs font-medium text-foreground-700">
            涉及事件或主题
          </label>
          <input
            id="tip-topic"
            name="event_topic"
            type="text"
            required
            placeholder="如：某小区加装电梯费用协商"
            className="w-full text-sm px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="tip-type" className="text-xs font-medium text-foreground-700">
            线索类型
          </label>
          <select
            id="tip-type"
            name="tip_type"
            required
            defaultValue=""
            className="w-full text-sm px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 cursor-pointer"
          >
            <option value="" disabled>
              请选择线索类型
            </option>
            <option value="亲历细节">亲历细节</option>
            <option value="时间节点">时间节点</option>
            <option value="公开来源链接">公开来源链接</option>
            <option value="后续进展">后续进展</option>
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="tip-detail" className="text-xs font-medium text-foreground-700">
          线索描述
        </label>
        <textarea
          id="tip-detail"
          name="detail"
          required
          maxLength={500}
          rows={5}
          onChange={(e) => setCount(e.target.value.length)}
          placeholder="请尽量写清时间、地点与经过，便于我们核实。最多 500 字。"
          className="w-full text-sm px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 resize-y"
        />
        <span className="text-[11px] text-foreground-500 text-right">{count}/500</span>
      </div>

      <div className="news-honeypot-field" aria-hidden="true">
        <label htmlFor="tips-form-contact-alt">请留空</label>
        <input
          id="tips-form-contact-alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-[11px] text-foreground-500 max-w-sm leading-relaxed">
          提交即表示你同意我们在核实过程中使用该线索，你的联系方式不会被公开。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          <i className="ri-send-plane-line" />
          {status === "submitting" ? "提交中…" : "提交线索"}
        </button>
      </div>

      {status !== "idle" && (
        <p
          className={`mt-4 text-sm rounded-md px-3.5 py-2.5 ${
            status === "error"
              ? "bg-primary-50 text-primary-800 border border-primary-200"
              : "bg-accent-50 text-accent-800 border border-accent-200"
          }`}
        >
          {message}
        </p>
      )}
    </form>
  );
}